import type {
  SortCountDiffRowDTO,
  SortCountScanEventDTO,
  SortCountSnapshotDTO,
  SortCountSummaryDTO,
  SortCountTargetDTO,
  SortCountValidationResultDTO,
} from '../../types/sortAndCount';

const normalizeCode = (value: any): string => String(value || '').trim().toUpperCase();

export const buildTargetMap = (targets: SortCountTargetDTO[]): Record<string, SortCountTargetDTO> => {
  const map: Record<string, SortCountTargetDTO> = {};
  (Array.isArray(targets) ? targets : []).forEach((target) => {
    const code = normalizeCode(target?.target_code);
    if (!code) return;
    if (!map[code]) {
      map[code] = {
        target_code: code,
        target_label: String(target?.target_label || code).trim() || code,
        expected_qty: Math.max(0, Number(target?.expected_qty || 0)),
        sorting_mark: String(target?.sorting_mark || '').trim() || undefined,
      };
      return;
    }
    map[code].expected_qty += Math.max(0, Number(target?.expected_qty || 0));
    if (!map[code].sorting_mark && target?.sorting_mark) {
      map[code].sorting_mark = String(target.sorting_mark).trim();
    }
  });
  return map;
};

export const buildCountedQtyByTarget = (events: SortCountScanEventDTO[]): Record<string, number> => {
  const map: Record<string, number> = {};
  (Array.isArray(events) ? events : []).forEach((event) => {
    const code = normalizeCode(event?.target_code);
    const qty = Number(event?.qty || 0);
    if (!code || !(qty > 0)) return;
    map[code] = (map[code] || 0) + qty;
  });
  return map;
};

export const buildDiffRows = (
  targets: SortCountTargetDTO[],
  events: SortCountScanEventDTO[],
): SortCountDiffRowDTO[] => {
  const targetMap = buildTargetMap(targets);
  const countedMap = buildCountedQtyByTarget(events);
  return Object.keys(targetMap).map((code) => {
    const target = targetMap[code];
    const expectedQty = Number(target?.expected_qty || 0);
    const countedQty = Number(countedMap[code] || 0);
    const diffQty = expectedQty - countedQty;
    return {
      target_code: code,
      target_label: target?.target_label || code,
      sorting_mark: target?.sorting_mark,
      expected_qty: expectedQty,
      counted_qty: countedQty,
      diff_qty: diffQty,
      done: diffQty <= 0,
    };
  });
};

export const buildSummary = (rows: SortCountDiffRowDTO[]): SortCountSummaryDTO => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const expectedTotal = safeRows.reduce((sum, row) => sum + Math.max(0, Number(row?.expected_qty || 0)), 0);
  const countedTotal = safeRows.reduce((sum, row) => sum + Math.max(0, Number(row?.counted_qty || 0)), 0);
  return {
    expected_total: expectedTotal,
    counted_total: countedTotal,
    diff_total: expectedTotal - countedTotal,
    done_targets: safeRows.filter((row) => Number(row?.diff_qty || 0) <= 0).length,
    total_targets: safeRows.length,
    over_count_targets: safeRows.filter((row) => Number(row?.diff_qty || 0) < 0).length,
    under_count_targets: safeRows.filter((row) => Number(row?.diff_qty || 0) > 0).length,
  };
};

export const validateBeforeSubmit = (rows: SortCountDiffRowDTO[]): SortCountValidationResultDTO => {
  const errors: string[] = [];
  const warnings: string[] = [];
  const safeRows = Array.isArray(rows) ? rows : [];
  if (safeRows.length === 0) {
    errors.push('缺少分拣清点目标，无法提交。');
  }
  const underRows = safeRows.filter((row) => Number(row?.diff_qty || 0) > 0);
  if (underRows.length > 0) {
    warnings.push(`仍有 ${underRows.length} 个目标未清点完成。`);
  }
  return {
    pass: errors.length === 0,
    errors,
    warnings,
  };
};

export const buildSnapshot = (
  targets: SortCountTargetDTO[],
  rows: SortCountDiffRowDTO[],
  events: SortCountScanEventDTO[],
): SortCountSnapshotDTO => ({
  schema_version: 1,
  kind: 'sort_and_count',
  created_at: new Date().toISOString(),
  targets: Array.isArray(targets) ? targets : [],
  rows: Array.isArray(rows) ? rows : [],
  summary: buildSummary(rows),
  events: Array.isArray(events) ? events : [],
});

export const makeScanEvent = (input: {
  scan_value: string;
  target_code: string;
  qty?: number;
  source?: 'scan' | 'manual' | 'import';
  container_code?: string;
}): SortCountScanEventDTO => ({
  id: `sce_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  scan_value: String(input?.scan_value || '').trim(),
  target_code: normalizeCode(input?.target_code),
  qty: Math.max(1, Number(input?.qty || 1)),
  source: (input?.source || 'scan'),
  container_code: String(input?.container_code || '').trim() || undefined,
  operated_at: new Date().toISOString(),
});

